import 'react-toastify/dist/ReactToastify.css';

import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { IconProvider } from '@icon-park/react/es/runtime';
import { memo, type ReactNode, useMemo, useState } from 'react';
import type { ToastContainerProps } from 'react-toastify';

import { THEME_MODES } from '@didaskill/domain/configurations/constants';

import { AVAILABLE_THEMES } from '../../../config/themes';
import { ThemeContext } from '../../../context/index';
import { DEFAULT_THEME } from '../../../themes/default/index';
import { ToastContainer } from '../../feedback/toast/ToastContainer';
import { ToastGlobalStyles } from '../../feedback/toast/ToastGlobalStyles';
import { ICON_CONFIG } from '../../general/icon/constants';
import { IconGlobalStyles } from '../../general/icon/IconGlobalStyles';
import { DialogsProvider } from '../dialogs-provider/DialogsProvider';
import { LayersProvider } from '../layers-provider/LayersProvider';

export type DesignSystemProviderProps = {
  defaultTheme?: THEME_MODES;
  rootElementSelector?: string;
  minLayerZIndex?: number;
  layerZIndexInterval?: number;
  children?: ReactNode;
  toastContainerProps?: ToastContainerProps;
};

export const DesignSystemProviderBase = ({
  defaultTheme = THEME_MODES.DEFAULT,
  rootElementSelector = '#root',
  toastContainerProps = null,
  minLayerZIndex = undefined,
  layerZIndexInterval = undefined,
  children = null,
}: DesignSystemProviderProps) => {
  const [theme, setTheme] = useState<THEME_MODES>(defaultTheme);

  const activeTheme = useMemo(() => {
    return AVAILABLE_THEMES[theme] || DEFAULT_THEME;
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      <EmotionThemeProvider theme={activeTheme}>
        <LayersProvider minLayerZIndex={minLayerZIndex} layerZIndexInterval={layerZIndexInterval}>
          <DialogsProvider rootElementSelector={rootElementSelector}>
            <IconProvider value={ICON_CONFIG}>
              {activeTheme.globalStylesComponent && <activeTheme.globalStylesComponent />}
              <IconGlobalStyles />
              <ToastGlobalStyles />

              {children}

              <ToastContainer {...toastContainerProps} />
            </IconProvider>
          </DialogsProvider>
        </LayersProvider>
      </EmotionThemeProvider>
    </ThemeContext.Provider>
  );
};

DesignSystemProviderBase.displayName = 'DesignSystemProvider';

export const DesignSystemProvider = memo(DesignSystemProviderBase);
